import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { normalizeItemName, sumEstimates } from '@tahi/core';
import { Button, MenuCard, Money } from '../../../ui';
import { AddItemForm } from '../components/AddItemForm';
import { BuySheet } from '../components/BuySheet';
import { copy } from '../copy';
import { quantityText, unitPriceText } from '../format';
import { useOpenItems, usePriceData, usePriceStats } from '../hooks';
import { CARD, FOCUS, H1, SCREEN } from '../styles';

/** The module's home: quick add, the open list with last prices, tick and buy (spec §6). */
export function ShoppingListScreen() {
  const items = useOpenItems();
  const priceData = usePriceData();
  const stats = usePriceStats();
  const [ticked, setTicked] = useState<ReadonlySet<string>>(new Set());
  const [buying, setBuying] = useState(false);

  const open = useMemo(() => items ?? [], [items]);
  // Items bought or removed elsewhere drop out of the selection.
  const selected = useMemo(() => open.filter((i) => ticked.has(i.id)), [open, ticked]);
  const estimate = sumEstimates(open);
  // Every trip has at least one line, so the trips with lines are all the trips.
  const tripCount = useMemo(
    () => new Set((priceData?.lines ?? []).map((l) => l.tripId)).size,
    [priceData],
  );

  function toggle(id: string) {
    setTicked((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <main className={SCREEN}>
      <header className="flex flex-col gap-1">
        <h1 className={H1}>{copy.moduleName}</h1>
        {open.length > 0 ? (
          <p className="text-sm text-muted" data-testid="shopping-summary">
            {copy.summary(open.length, estimate)}
          </p>
        ) : null}
      </header>

      <AddItemForm priceData={priceData} />

      {items === undefined ? (
        <p className="text-muted">{copy.loading}</p>
      ) : open.length === 0 ? (
        <p className="text-muted">{copy.empty}</p>
      ) : (
        <ul
          aria-label={copy.moduleName}
          className={`${CARD} flex flex-col divide-y divide-divider py-1`}
        >
          {open.map((i) => {
            const s = stats?.get(normalizeItemName(i.name));
            const details = [quantityText(i.quantity, i.unit), i.store ?? ''].filter(Boolean);
            const priceLine = s
              ? [
                  s.basis === 'unit' && s.unit
                    ? copy.lastPriceText(unitPriceText(s.lastValue, s.unit))
                    : copy.lastPrice(s.last.priceMinor),
                  s.cheapestStore ? copy.cheapestAt(s.cheapestStore) : '',
                ]
                  .filter(Boolean)
                  .join(' · ')
              : '';
            return (
              <li key={i.id} className="flex items-center gap-1 py-1">
                <label className="flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center">
                  <input
                    type="checkbox"
                    checked={ticked.has(i.id)}
                    onChange={() => toggle(i.id)}
                    aria-label={i.name}
                    className={`h-6 w-6 accent-[var(--accent)] ${FOCUS}`}
                  />
                </label>
                <Link
                  to={`/m/shopping/items/${i.id}`}
                  className={`flex min-h-11 min-w-0 flex-1 items-center justify-between gap-3 rounded-xl pr-1 ${FOCUS}`}
                >
                  <span className="min-w-0">
                    <span className="block truncate text-fg">{i.name}</span>
                    {details.length > 0 ? (
                      <span className="block truncate text-xs text-muted">
                        {details.join(' · ')}
                      </span>
                    ) : null}
                    {priceLine ? (
                      <span className="block truncate text-xs text-primary">{priceLine}</span>
                    ) : null}
                  </span>
                  {i.estimateMinor ? (
                    <Money
                      minor={i.estimateMinor}
                      cents="auto"
                      animate={false}
                      className="shrink-0 text-sm text-muted"
                    />
                  ) : null}
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      {selected.length > 0 ? (
        <div className="sticky bottom-[calc(88px+env(safe-area-inset-bottom))] z-20">
          <Button variant="primary" block className="min-h-12" onClick={() => setBuying(true)}>
            {copy.buyButton(selected.length, sumEstimates(selected))}
          </Button>
        </div>
      ) : null}

      <MenuCard
        label={copy.pages}
        items={[
          {
            to: '/m/shopping/trips',
            label: copy.pastTrips,
            icon: 'receipt',
            value: tripCount > 0 ? copy.tripsCount(tripCount) : null,
          },
          {
            to: '/m/shopping/prices',
            label: copy.prices,
            icon: 'tag',
            value: stats && stats.size > 0 ? copy.itemsCount(stats.size) : null,
          },
        ]}
      />

      {buying && selected.length > 0 ? (
        <BuySheet
          items={selected}
          onClose={() => setBuying(false)}
          onBought={() => {
            setBuying(false);
            setTicked(new Set());
          }}
        />
      ) : null}
    </main>
  );
}
